import React, { useState, useRef } from "react"
import {DfLogo, MailIcon, PasswordIcon, EyeIcon, EyeOffIcon} from "assets/icons.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext.jsx";
import { useTranslation } from "react-i18next";
import { setUser, setTokens, clearAuthState } from "utils/auth.js";
import { apiUrl } from "config/api";
import i18n from "../../i18n";

// Try to retrieve an access token saved by setTokens
function getStoredAccessToken() {
  try {
    const keys = ['access_token', 'accessToken', 'token'];
    for (const k of keys) {
      const v = window.localStorage.getItem(k);
      if (v) return v;
    }
  } catch {}
  return null;
}

function routeForUserStatus(rawStatus) {
  const status = String(rawStatus || "").toLowerCase();
  if (status === "active") return "/news";
  if (status === "onboarding") return "/onboarding";
  if (status === "initializing") return "/news";
  return null;
}

// --- Main App Component ---

export default function Login() {
    const nav = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { t } = useTranslation();

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successOpen, setSuccessOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [emailErr, setEmailErr] = useState("");
    const [passwordErr, setPasswordErr] = useState("");

    // Opening the login view should always clear any previous auth state/tokens
    React.useEffect(() => {
      clearAuthState();
    }, []);

    // One-time registration success popup (after redirect from register page).
    React.useEffect(() => {
      const showSuccess = Boolean(location?.state?.registrationSuccess);
      if (!showSuccess) return;
      try {
        const preferred = String(location?.state?.registrationPreferredLanguage || "").toLowerCase();
        if (preferred === "de" || preferred === "en" || preferred === "zh") {
          localStorage.setItem("df_language", preferred);
          i18n.changeLanguage(preferred);
        }
      } catch {}
      const message = String(location?.state?.registrationMessage || t("auth.register.successMessage"));
      setSuccessMsg(message);
      setSuccessOpen(true);
      nav(location.pathname, { replace: true, state: {} });
    }, [location?.pathname, location?.state, nav]);

    // Check if already authenticated and redirect accordingly
    React.useEffect(() => {
      let cancelled = false;
      (async () => {
        const token = getStoredAccessToken();
        if (!token) return; // No token yet → don't probe and cause a 401 loop
        try {
          const meRes = await fetch('/api/auth/me', {
            credentials: 'include',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!cancelled && meRes.ok) {
            const me = await meRes.json();
            const route = routeForUserStatus(me?.status);
            if (route) {
              window.location.replace(route);
              return;
            }
          }
        } catch {}
      })();
      return () => { cancelled = true; };
    }, []);

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const handleLogin = async (e) => {
      e.preventDefault();
      // client-side required field validation
      const missingEmail = !email || !email.trim();
      const missingPassword = !password || !password.trim();
      setEmailErr(missingEmail ? t("auth.login.error.emailRequired") : '');
      setPasswordErr(missingPassword ? t("auth.login.error.passwordRequired") : '');
      if (missingEmail || missingPassword) {
        return; // stop submit and show inline errors
      }
      try {
        const res = await fetch(apiUrl("/api/auth/login/"), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          credentials: 'include',
        });
        if (!res.ok) {
          const msg = await res.text();
          throw new Error(msg || `HTTP ${res.status}`);
        }
        const data = await res.json();
        const { access_token, refresh_token, user } = data || {};
        if (!access_token || !refresh_token || !user?.id) {
          throw new Error(t("auth.login.error.invalidResponse"));
        }
        // Persist tokens and minimal user info
        setTokens(access_token, refresh_token);
        setUser(user.id, user.email || email);

        // Strictly enforce using the server-confirmed /auth/me for redirect logic
        let me;
        try {
          const meRes = await fetch(apiUrl("/api/auth/me"), {
            headers: { Authorization: `Bearer ${access_token}` },
            credentials: 'include',
          });
          if (!meRes.ok) throw new Error('Auth verification failed after login');
          me = await meRes.json();
        } catch (e) {
          setErrorMsg(t("auth.login.error.sessionVerifyFailed"));
          setErrorOpen(true);
          return;
        }

        // Update auth context if it supports passing tokens/user
        try {
          await Promise.resolve(login?.({ user: me || user, accessToken: access_token, refreshToken: refresh_token }));
        } catch {}

        const route = routeForUserStatus(me?.status || user?.status);
        if (route) {
          window.location.replace(route);
        } else {
          setErrorMsg(t("auth.login.error.accountInactive"));
          setErrorOpen(true);
        }
      } catch (err) {
        const message = (() => {
          const raw = err?.message || String(err);
          try {
            const parsed = JSON.parse(raw);
            if (parsed?.detail === 'Email not found') return t("auth.login.error.emailNotFound");
            if (parsed?.detail === 'Invalid credentials') return t("auth.login.error.invalidCredentials");
            if (typeof parsed?.detail === 'string') return parsed.detail;
          } catch {}
          if (/Email not found/i.test(raw)) return t("auth.login.error.emailNotFound");
          if (/Invalid credentials/i.test(raw)) return t("auth.login.error.invalidCredentials");
          return raw.startsWith('HTTP') ? t("auth.login.error.generic") : raw;
        })();
        setErrorMsg(message);
        setErrorOpen(true);
      }
    };

    // The background video URL
    const backgroundVideoUrl = "/background_new.mp4";

    // User Info Card Controls
    //const [userInfoMode, setUserInfoMode] = useState<"standard" | "polar" | "prominent" | "shader">("standard")

    const containerRef = useRef(null)

    const scrollingOverBrightSection = window.scrollY > 230 && window.scrollY < 500;

    return (
  <div className="login-page" style={{ position: 'relative', width: '100%', minHeight: '100vh', overflow: 'hidden', fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial' }}>
    {/* Background video */}
    <video
      autoPlay
      loop
      muted
      playsInline
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
    >
      <source src={backgroundVideoUrl} type="video/mp4" />
    </video>

    {/* Overlay blur */}
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(37,37,37,0.25)', backdropFilter: 'blur(3.7px)', WebkitBackdropFilter: 'blur(3.7px)', zIndex: 1 }} />

    {/* Center Glass Card */}
    <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -55%)', width: '92%', maxWidth: 507, borderRadius: 25, overflow: 'hidden', zIndex: 2, boxShadow: '0px 0px 3px 0px #f2f2f2 inset, 0px 0px 3px 0px #ffffff80 inset, -1px -1px 0.5px -1px #ffffff inset, 1px 1px 0.5px -1px #ffffff inset, -1px -1px 0px -0.5px #262626 inset, 1px 1px 0px -0.5px #333333 inset', background: 'rgba(0, 0, 0, 0.17)', backgroundBlendMode: 'screen, luminosity, darken' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,8,8,0.03)', backdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'relative', padding: 24 }}>
        {/* Brand Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 232 }}>
                        <DfLogo style={{ width: '100%', height: 'auto', display: 'block' }} />
                      </div>
                    </div>

        {/* Form */}
        <form onSubmit={handleLogin}>
          {/* Email */}
          <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: emailErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
              <MailIcon />
              <input
                type="email"
                name="email"
                id="email"
                placeholder={t("auth.login.email.placeholder")}
                autoComplete="off"
                spellCheck={false}
                value={email}
                onChange={(e) => { const v = e.target.value; setEmail(v); if (emailErr && v && v.trim()) setEmailErr(''); }}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14, fontStyle: 'italic' }}
              />
            </div>
          </div>
          {emailErr && (
            <div style={{ color: '#ff4d4f', fontSize: 12, lineHeight: '16px', margin: '6px 6px 10px 6px' }}>{emailErr}</div>
          )}

          {/* Password */}
          <div style={{ position: 'relative', width: '100%', height: 42, borderRadius: 24, overflow: 'hidden', marginBottom: 16, boxShadow: passwordErr ? '0 0 0 1px #ff4d4f inset, -2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)' : '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)', background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51,51,51,0.79), rgba(153,153,153,0.30)', backgroundBlendMode: 'screen, luminosity, darken' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', paddingLeft: 5, paddingRight: 16, gap: 12 }}>
              <PasswordIcon />
              <input
                type={passwordVisible ? 'text' : 'password'}
                name="password"
                id="password"
                placeholder={t("auth.login.password.placeholder")}
                autoComplete="new-password"
                spellCheck={false}
                value={password}
                onChange={(e) => { const v = e.target.value; setPassword(v); if (passwordErr && v && v.trim()) setPasswordErr(''); }}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 14 }}
              />
              <button type="button" onClick={togglePasswordVisibility} style={{ background: 'transparent', border: 'none', outline: 'none', cursor: 'pointer', padding: 4 }}>
                {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          {passwordErr && (
            <div style={{ color: '#ff4d4f', fontSize: 12, lineHeight: '16px', margin: '6px 6px 10px 6px' }}>{passwordErr}</div>
          )}

          {/* Login Button */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
            <button
              type="submit"
              style={{
                display: 'flex',
                width: 140,
                height: 36,
                padding: '10px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
                borderRadius: 5,
                background: 'transparent',
                backgroundBlendMode: 'plus-lighter, luminosity, darken',
                boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
                color: '#FFF',
                letterSpacing: 1,
                fontWeight: 500,
                border: 'none'
              }}
            >
              {t("auth.login.button")}
            </button>
          </div>
        </form>
      </div>
    </div>

    {/* Bottom content */}
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1, display: "none" }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 16px 24px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
        <div style={{ width: '100%', maxWidth: 577, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 200 }}>
                          <DfLogo style={{ width: '100%', height: 'auto', display: 'block' }} />
                        </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, color: '#F0F0F0', fontSize: 14, fontWeight: 600 }}>
            <div>{t("footer.nav.marketInsights")}</div>
            <div>{t("footer.nav.dataSolutions")}</div>
            <div>{t("footer.nav.analyticsTools")}</div>
            <div>{t("footer.nav.clientSupport")}</div>
            <div>{t("footer.nav.contactUs")}</div>
          </div>
        </div>
        <div style={{ width: '100%' }}>
          <div style={{ height: 0, borderTop: '1px solid rgba(240,240,240,0.9)' }} />
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24, color: '#F0F0F0', fontSize: 14, textDecoration: 'underline' }}>
            <div>{t("footer.legal.privacyPolicy")}</div>
            <div>{t("footer.legal.termsOfService")}</div>
            <div>{t("footer.legal.cookiesSettings")}</div>
          </div>
        </div>
      </div>
    </div>
    {/* Error Modal */}
    {errorOpen && (
      <div role="dialog" aria-modal="true" aria-labelledby="loginErrorTitle"
           style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* backdrop */}
        <div onClick={() => setErrorOpen(false)}
             style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />

        {/* modal card */}
        <div style={{ position: 'relative', zIndex: 51, width: '92%', maxWidth: 420, borderRadius: 16, overflow: 'hidden',
                      background: 'rgba(0, 0, 0, 0.17)',
                      backgroundBlendMode: 'screen, luminosity, darken', boxShadow: '0px 0px 3px 0px #f2f2f2 inset, 0px 0px 3px 0px #ffffff80 inset, -1px -1px 0.5px -1px #ffffff inset, 1px 1px 0.5px -1px #ffffff inset, -1px -1px 0px -0.5px #262626 inset, 1px 1px 0px -0.5px #333333 inset' }}>
          <div style={{ padding: 20 }}>
            <div id="loginErrorTitle" style={{ color: '#F0F0F0', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{t("auth.login.error.title")}</div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, lineHeight: '20px' }}>{errorMsg}</div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setErrorOpen(false)}
                style={{
                  display: 'flex', height: 32, padding: '8px 14px', alignItems: 'center', gap: 8, borderRadius: 6, border: 'none', cursor: 'pointer',
                  background: 'transparent', color: 'rgba(255,255,255,0.85)', textDecoration: 'underline'
                }}>{t("auth.login.error.close")}</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {/* Success Modal */}
    {successOpen && (
      <div role="dialog" aria-modal="true" aria-labelledby="loginSuccessTitle"
           style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onClick={() => setSuccessOpen(false)}
             style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }} />

        <div style={{ position: 'relative', zIndex: 51, width: '92%', maxWidth: 420, borderRadius: 16, overflow: 'hidden',
                      background: 'rgba(0, 0, 0, 0.17)',
                      backgroundBlendMode: 'screen, luminosity, darken', boxShadow: '0px 0px 3px 0px #f2f2f2 inset, 0px 0px 3px 0px #ffffff80 inset, -1px -1px 0.5px -1px #ffffff inset, 1px 1px 0.5px -1px #ffffff inset, -1px -1px 0px -0.5px #262626 inset, 1px 1px 0px -0.5px #333333 inset' }}>
          <div style={{ padding: 20 }}>
            <div id="loginSuccessTitle" style={{ color: '#F0F0F0', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>{t("auth.register.successTitle")}</div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, lineHeight: '20px' }}>{successMsg}</div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setSuccessOpen(false)}
                style={{
                  display: 'flex', height: 32, padding: '8px 16px', justifyContent: 'center', alignItems: 'center', gap: 10, flexShrink: 0,
                  borderRadius: 5, border: 'none', cursor: 'pointer', color: '#FFF', letterSpacing: 0.5, fontWeight: 500,
                  background: 'transparent',
                  backgroundBlendMode: 'plus-lighter, luminosity, darken',
                  boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)'
                }}>
                {t("auth.register.successCta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
    );
}
